import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  MonitorPlay,
  PlugZap,
  Power,
  RefreshCw,
  Search,
  Server,
} from 'lucide-react';
import { api, type AppStreamFleet, type AppStreamSession } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { formatDateTime, formatDuration, formatNumber, formatUntil } from '../lib/format';
import { useAuth } from '../contexts/use-auth';
import { cn } from '../lib/utils';

// Live view, so it polls. Sessions turn over on the order of minutes and the
// call is four AppStream Describes behind a warm directory cache, so this is
// cheap enough to leave running on a support desk's second monitor.
const REFRESH_MS = 15_000;

// One table with a filter rather than split customer/internal tables: the
// question this page answers is "who is on the fleet right now", and two tables
// make the totals stop being comparable at a glance.
type Audience = 'all' | 'external' | 'internal';
const AUDIENCES: { value: Audience; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'external', label: 'Customers' },
  { value: 'internal', label: 'Internal' },
];

const getSessionValue = (item: AppStreamSession, key: string): unknown => {
  switch (key) {
    case 'userId':
      return item.userId;
    case 'organization':
      return item.organizationName ?? '';
    case 'id':
      return item.id;
    case 'connectionState':
      // Disconnected first when ascending: those are the ones holding capacity
      // for nobody, which is the reason to open this page.
      return item.connectionState === 'CONNECTED' ? 1 : 0;
    case 'startTime':
      return item.startTime ?? '';
    case 'instanceId':
      return item.instanceId ?? '';
    case 'maxExpirationTime':
      return item.maxExpirationTime ?? '';
    default:
      return undefined;
  }
};

export function AppStreamSessions() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canTerminate = can('appstream.terminate');

  const sessions = useQuery({
    queryKey: ['appstream', 'sessions'],
    queryFn: () => api.listAppStreamSessions(),
    refetchInterval: REFRESH_MS,
  });

  const terminate = useMutation({
    mutationFn: (session: AppStreamSession) => api.terminateAppStreamSession(session.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['appstream', 'sessions'] });
    },
  });

  const [q, setQ] = useState('');
  const [onlyDisconnected, setOnlyDisconnected] = useState(false);
  const [audience, setAudience] = useState<Audience>('all');

  const items = useMemo(() => sessions.data?.items ?? [], [sessions.data]);
  const fleets = sessions.data?.fleets ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((s) => {
      if (onlyDisconnected && s.connectionState === 'CONNECTED') return false;
      if (audience === 'external' && s.internal) return false;
      if (audience === 'internal' && !s.internal) return false;
      if (!needle) return true;
      return (
        s.userId.toLowerCase().includes(needle) ||
        (s.organizationName ?? '').toLowerCase().includes(needle) ||
        s.id.toLowerCase().includes(needle) ||
        s.fleetName.toLowerCase().includes(needle) ||
        (s.instanceId ?? '').toLowerCase().includes(needle) ||
        (s.privateIp ?? '').toLowerCase().includes(needle)
      );
    });
  }, [items, q, onlyDisconnected, audience]);

  const { sortedItems, sort, toggle } = useSort(filtered, getSessionValue, {
    key: 'startTime',
    direction: 'desc',
  });

  const connected = items.filter((s) => s.connectionState === 'CONNECTED').length;
  const disconnected = items.length - connected;
  const orgCount = new Set(
    items.filter((s) => !s.internal).map((s) => s.organizationId ?? `@${s.userId}`),
  ).size;
  const internalCount = items.filter((s) => s.internal).length;

  const confirmTerminate = (session: AppStreamSession) => {
    const who = session.organizationName
      ? `${session.userId} (${session.organizationName})`
      : session.userId;
    const lines = [
      `Terminate the streaming session for ${who}?`,
      '',
      `Session ${session.id}`,
      `${session.fleetName} · started ${formatDateTime(session.startTime)}`,
      '',
      session.connectionState === 'CONNECTED'
        ? 'They are connected right now. Their application closes immediately and anything unsaved in it is lost.'
        : 'They are disconnected but the session is still holding an instance. Terminating frees it.',
      '',
      'This cannot be undone. They would have to start a new session.',
    ];
    if (confirm(lines.join('\n'))) terminate.mutate(session);
  };

  const columnCount = canTerminate ? 8 : 7;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Streaming sessions</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live Amazon WorkSpaces Applications sessions, attributed to the customer using them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sessions.data?.asOf && (
            <span className="text-xs text-muted-foreground">
              as of {formatDateTime(sessions.data.asOf)}
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => void sessions.refetch()}
            disabled={sessions.isFetching}
          >
            <RefreshCw className={cn('h-4 w-4', sessions.isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {sessions.error && (
        <p className="text-sm text-destructive">{(sessions.error as Error).message}</p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard title="Live sessions" icon={<MonitorPlay className="h-4 w-4 text-muted-foreground" />}>
          <div className="text-2xl font-bold tabular-nums">{formatNumber(items.length)}</div>
          <p className="text-xs text-muted-foreground">
            {formatNumber(connected)} connected · {formatNumber(disconnected)} disconnected
          </p>
        </StatCard>
        <StatCard title="Organizations" icon={<Building2 className="h-4 w-4 text-muted-foreground" />}>
          <div className="text-2xl font-bold tabular-nums">{formatNumber(orgCount)}</div>
          <p className="text-xs text-muted-foreground">
            customers streaming now · {formatNumber(internalCount)} internal session
            {internalCount === 1 ? '' : 's'}
          </p>
        </StatCard>
        {fleets.map((fleet) => (
          <FleetCard key={fleet.name} fleet={fleet} />
        ))}
      </div>

      {sessions.data?.directoryTruncated && (
        <p className="flex items-start gap-2 text-sm text-caution">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          The X5 user roster was too large to read in one pass, so some sessions may show without an
          organization.
        </p>
      )}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <div className="relative w-72">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search user, org, session, instance…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-0.5 rounded-md border border-line p-0.5">
            {AUDIENCES.map((a) => (
              <button
                key={a.value}
                type="button"
                onClick={() => setAudience(a.value)}
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                  audience === a.value
                    ? 'bg-accent-soft text-accent'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {a.label}
              </button>
            ))}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={onlyDisconnected}
              onChange={(e) => setOnlyDisconnected(e.target.checked)}
              className="h-3.5 w-3.5 accent-[var(--color-accent)]"
            />
            Disconnected only
          </label>
          <span className="ml-auto text-xs text-muted-foreground">
            {formatNumber(sortedItems.length)} of {formatNumber(items.length)}
          </span>
        </div>

        {terminate.isError && (
          <div className="border-b border-line px-4 py-2 text-sm text-destructive">
            {(terminate.error as Error).message}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <SortableHeader label="User" sortKey="userId" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Organization"
                  sortKey="organization"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader label="Session" sortKey="id" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Connected"
                  sortKey="connectionState"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader label="Started" sortKey="startTime" sort={sort} onToggle={toggle} />
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Duration</th>
                <SortableHeader
                  label="Expires"
                  sortKey="maxExpirationTime"
                  sort={sort}
                  onToggle={toggle}
                  align="right"
                />
                {canTerminate && <th className="px-4 py-3 text-right font-medium text-muted-foreground" />}
              </tr>
            </thead>
            <tbody>
              {sessions.isLoading && (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {sortedItems.map((s) => {
                const pending = terminate.isPending && terminate.variables?.id === s.id;
                return (
                  <tr
                    key={s.id}
                    className={cn(
                      'border-b border-border transition-colors last:border-0 hover:bg-muted/50',
                      s.internal && 'bg-accent-soft/40 shadow-[inset_2px_0_0_var(--color-accent)]',
                    )}
                  >
                    <td className="px-4 py-3 font-medium">{s.userId || '—'}</td>
                    <td className="px-4 py-3">
                      {s.organizationName ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-fg-2">{s.organizationName}</span>
                          {s.internal && (
                            <Badge variant="outline" className="border-accent-line text-accent">
                              Internal
                            </Badge>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic">Unattributed</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-muted-foreground" title={s.id}>
                        {s.id.slice(0, 8)}
                      </span>
                      {s.instanceId && (
                        <div className="font-mono text-xs text-muted-foreground/70">
                          {s.instanceId}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <ConnectionBadge session={s} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {formatDateTime(s.startTime)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                      {formatDuration(s.startTime, null)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap text-muted-foreground">
                      {formatUntil(s.maxExpirationTime)}
                    </td>
                    {canTerminate && (
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={pending}
                          onClick={() => confirmTerminate(s)}
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Power className={cn('h-3.5 w-3.5', pending && 'animate-pulse')} />
                          {pending ? 'Ending…' : 'Terminate'}
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {!sessions.isLoading && sortedItems.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-6 text-center text-muted-foreground">
                    {items.length === 0 ? 'No live sessions.' : 'No sessions match that filter.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <PlugZap className="h-4 w-4 text-muted-foreground" />
            Reading this page
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            A <span className="text-foreground">disconnected</span> session still holds a fleet
            instance until the disconnect timeout elapses, so it counts against capacity even though
            nobody is using it. Those are usually the ones worth terminating.
          </p>
          <p>
            Per-session CPU and memory are not published by AppStream, and the streaming instances
            run in an AWS-managed account, so there is no host metric to show. Fleet{' '}
            <span className="text-foreground">in-use vs. running</span> above is the capacity signal.
          </p>
          <p>
            Every termination is written to the audit log with the operator, the user, and the
            organization affected — before the session is stopped.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ConnectionBadge({ session }: { session: AppStreamSession }) {
  if (session.state === 'EXPIRED') return <Badge variant="secondary">Expired</Badge>;
  if (session.state === 'PENDING') return <Badge variant="outline">Starting</Badge>;
  return session.connectionState === 'CONNECTED' ? (
    <Badge variant="success">Connected</Badge>
  ) : (
    <Badge variant="warning">Disconnected</Badge>
  );
}

function FleetCard({ fleet }: { fleet: AppStreamFleet }) {
  const running = fleet.running ?? 0;
  const inUse = fleet.inUse ?? 0;
  const pct = running > 0 ? Math.round((inUse / running) * 100) : 0;
  const bar = pct >= 90 ? 'bg-destructive' : pct >= 70 ? 'bg-warn' : 'bg-ok';

  return (
    <StatCard title={fleet.name} icon={<Server className="h-4 w-4 text-muted-foreground" />}>
      <div className="text-2xl font-bold tabular-nums">
        {formatNumber(inUse)}
        <span className="text-base font-normal text-muted-foreground">/{formatNumber(running)}</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-raise">
        <div className={cn('h-full rounded-full', bar)} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        {pct}% in use · {formatNumber(fleet.available)} free · {fleet.instanceType ?? '—'}
      </p>
    </StatCard>
  );
}

function StatCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="truncate text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
